/**
 * Tempo and pitch arithmetic — the part of §9 that is real.
 *
 * WHAT THIS DOES. Given a track at 74 BPM and a request for 92, it works out that the
 * result would play 1.24× faster and last 3:17 instead of 4:05; given +7 semitones, that
 * the frequency ratio is 1.498 and the interval is a perfect fifth. All of that is
 * arithmetic on numbers the app already has, it is correct, and it needs no server — so
 * it is done here, unit tested, and kept out of the component.
 *
 * WHAT THIS DOES NOT DO, and cannot: change any audio. Time stretching and pitch
 * shifting are DSP operations on a file, and there is no file — `Song.audioUrl` is null
 * for every song, and the Lyria 3 Interactions API takes no audio as input, so a stretch
 * cannot be expressed as a generation request either. See api/audioEditApi.ts, which
 * records `tempo` and `pitch` as missing capabilities and throws rather than resolving.
 *
 * So nothing here returns a Song, a version, or a new `length` for the song record. The
 * resulting duration is returned as a NUMBER for display next to the slider — a
 * prediction of what the operation would produce, labelled as such in the panel. Writing
 * it onto the song would be the lie: the track would claim 3:17 while the audio, if it
 * ever arrives, is still 4:05 of it.
 *
 * Modelled on lib/trim.ts, deliberately: same division of labour (parse and validate
 * here, render there), same strictness about input, same refusal to invent a result.
 */

/** The slider's bounds, in BPM. */
export const MIN_BPM = 40;
export const MAX_BPM = 220;

/**
 * The pitch range, in semitones. ±12 — one octave either way, which is the range the
 * brief asks for and also the point past which a shift stops sounding like the same
 * performance.
 */
export const MAX_SEMITONES = 12;

/** What `resolveTempo` concluded: a change, or the reason there isn't one. */
export interface TempoResult {
  /** The validated target BPM, or null when the input does not describe one. */
  bpm: number | null;
  /**
   * Playback ratio, `toBpm / fromBpm`. 1.24 means 24% faster. Null when there is no
   * valid target, and 1 is a legitimate value — asking for the BPM it is already at is
   * valid input describing no change, which the panel reports rather than rejects.
   */
  ratio: number | null;
  /** How long the track would be afterwards, in seconds. Null without a duration. */
  seconds: number | null;
  /** The single most useful complaint, in the user's words, or null. */
  error: string | null;
}

/**
 * Reads a BPM a user typed or dragged.
 *
 * Whole numbers only. A slider produces integers and the field beside it should accept
 * the same values, so "92.5" is rejected rather than rounded — silently changing what
 * someone typed is how a readout stops matching its input.
 */
export function parseBpm(input: string): number | null {
  const text = input.trim();
  if (!text) return null;
  if (!/^\d{1,3}$/.test(text)) return null;
  return Number(text);
}

/** Clamps a BPM into the supported range. */
export function clampBpm(bpm: number): number {
  return Math.max(MIN_BPM, Math.min(MAX_BPM, Math.round(bpm)));
}

/** Clamps a semitone offset into ±12, rounding to whole semitones. */
export function clampSemitones(semitones: number): number {
  return Math.max(-MAX_SEMITONES, Math.min(MAX_SEMITONES, Math.round(semitones)));
}

/**
 * Validates a requested tempo against the track's own.
 *
 * `fromBpm` is the song's recorded BPM and `duration` its length in seconds. Both can be
 * missing or zero on a song that never reported them, and that is reported rather than
 * treated as 1 — a ratio against a zero BPM is not a number anybody should see.
 */
export function resolveTempo(input: string, fromBpm: number, duration: number): TempoResult {
  if (!(fromBpm > 0)) {
    return {
      bpm: null,
      ratio: null,
      seconds: null,
      error: 'This track has no recorded tempo to change.',
    };
  }

  const bpm = parseBpm(input);
  if (bpm === null) {
    return { bpm: null, ratio: null, seconds: null, error: 'Enter a tempo in BPM, like 120.' };
  }
  if (bpm < MIN_BPM || bpm > MAX_BPM) {
    return {
      bpm: null,
      ratio: null,
      seconds: null,
      error: `Tempo must be between ${MIN_BPM} and ${MAX_BPM} BPM.`,
    };
  }

  const ratio = bpm / fromBpm;
  return {
    bpm,
    ratio,
    // A faster tempo is a shorter track: duration scales by the inverse of the ratio.
    // Null rather than 0 when the length is unknown, so the panel can omit the readout
    // instead of promising a zero-second result.
    seconds: duration > 0 ? duration / ratio : null,
    error: null,
  };
}

/**
 * The frequency ratio of a semitone offset.
 *
 * Equal temperament: each semitone is the twelfth root of two, so twelve of them double
 * the frequency. +12 → 2, −12 → 0.5, 0 → 1.
 */
export function semitoneRatio(semitones: number): number {
  return Math.pow(2, semitones / 12);
}

/** Interval names, indexed by semitone distance from unison. */
const INTERVALS = [
  'unison',
  'minor 2nd',
  'major 2nd',
  'minor 3rd',
  'major 3rd',
  'perfect 4th',
  'tritone',
  'perfect 5th',
  'minor 6th',
  'major 6th',
  'minor 7th',
  'major 7th',
  'octave',
];

/**
 * A semitone offset in musical words — "+7 semitones (perfect 5th up)".
 *
 * Named rather than left as a bare number because ±7 and ±5 are the two shifts anybody
 * actually reaches for and the interval is what makes them recognisable. The direction
 * is stated in words as well as sign: "−5" and "down" are the same fact, and a screen
 * reader given only the minus reads it as a hyphen.
 */
export function describeSemitones(semitones: number): string {
  const n = clampSemitones(semitones);
  if (n === 0) return 'No change (unison)';
  const interval = INTERVALS[Math.abs(n)] ?? `${Math.abs(n)} semitones`;
  const sign = n > 0 ? '+' : '−';
  const direction = n > 0 ? 'up' : 'down';
  const plural = Math.abs(n) === 1 ? 'semitone' : 'semitones';
  return `${sign}${Math.abs(n)} ${plural} (${interval} ${direction})`;
}

/**
 * A tempo ratio as a percentage change — "24% faster", "12% slower", "no change".
 *
 * Rounded to whole percent: the underlying ratio is exact and a readout claiming 23.7%
 * would imply a precision the operation does not have, since the target BPM is itself an
 * integer approximation of what the user wants.
 */
export function describeTempoChange(ratio: number): string {
  const percent = Math.round(Math.abs(ratio - 1) * 100);
  if (percent === 0) return 'No change';
  return ratio > 1 ? `${percent}% faster` : `${percent}% slower`;
}
